import { useState } from 'react';

import { LibraryPicker } from '@/components/library-picker';
import { RichTextEditor } from '@/components/rich-text-editor';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { MediaRef } from '@/lib/media';

const STEPS = ['Details', 'Bio'] as const;

export interface ArtistValues {
  name: string;
  discipline: string;
  bio: string;
  email: string;
  avatarMediaId: string | null;
  avatarUrl: string | null;
}

interface ArtistDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  initialValues?: Partial<ArtistValues>;
  onSubmit: (values: ArtistValues) => void;
}

export function ArtistDialog({
  open,
  onOpenChange,
  mode,
  initialValues,
  onSubmit,
}: ArtistDialogProps) {
  const [name, setName] = useState(initialValues?.name ?? '');
  const [discipline, setDiscipline] = useState(initialValues?.discipline ?? '');
  const [bio, setBio] = useState(initialValues?.bio ?? '');
  const [email, setEmail] = useState(initialValues?.email ?? '');
  const [avatar, setAvatar] = useState<MediaRef | null>(
    initialValues?.avatarMediaId && initialValues?.avatarUrl
      ? { id: initialValues.avatarMediaId, url: initialValues.avatarUrl }
      : null,
  );
  const [step, setStep] = useState(0);

  const isEdit = mode === 'edit';
  const isLast = step === STEPS.length - 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          className="grid gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            if (!isLast) {
              setStep((value) => value + 1);
              return;
            }
            onSubmit({
              name,
              discipline,
              bio,
              email,
              avatarMediaId: avatar?.id ?? null,
              avatarUrl: avatar?.url ?? null,
            });
            onOpenChange(false);
          }}
        >
          <DialogHeader>
            <DialogTitle>{isEdit ? 'Edit artist' : 'New artist'}</DialogTitle>
            <DialogDescription>
              Step {step + 1} of {STEPS.length} · {STEPS[step]}
            </DialogDescription>
          </DialogHeader>

          {step === 0 && (
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label>Avatar</Label>
                <LibraryPicker value={avatar} onChange={setAvatar} types={['image']} />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="artist-name">Name</Label>
                <Input
                  id="artist-name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="e.g. Mara Oliveira"
                  required
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="artist-discipline">Discipline</Label>
                <Input
                  id="artist-discipline"
                  value={discipline}
                  onChange={(event) => setDiscipline(event.target.value)}
                  placeholder="e.g. Painting"
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="artist-email">Email</Label>
                <Input
                  id="artist-email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="artist@example.com"
                />
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="grid gap-2">
              <Label>Bio</Label>
              <RichTextEditor value={bio} onChange={setBio} placeholder="Short biography" />
            </div>
          )}

          <DialogFooter className="justify-between pt-2 sm:justify-between">
            <div>
              {step > 0 && (
                <Button type="button" variant="ghost" onClick={() => setStep((value) => value - 1)}>
                  Back
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              {step === 0 && (
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
              )}
              <Button type="submit">
                {!isLast ? 'Next' : isEdit ? 'Save changes' : 'Create artist'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
